import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { Button } from './button';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface FabConfig {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}

interface FabContextValue {
  config: FabConfig | null;
  setConfig: (config: FabConfig | null) => void;
}

const FabContext = createContext<FabContextValue>({ config: null, setConfig: () => {} });

/** Provider que deve envolver o Outlet para que páginas possam registrar o FAB. */
export function FabProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<FabConfig | null>(null);
  return (
    <FabContext.Provider value={{ config, setConfig }}>
      {children}
    </FabContext.Provider>
  );
}

/**
 * Renderiza o FAB flutuante no canto inferior direito (mobile only).
 * Deve ser colocado no SidebarLayout como sibling do header/main.
 */
export function FabRenderer({ className }: { className?: string }) {
  const { config } = useContext(FabContext);
  if (!config) return null;
  return (
    <Button
      size="icon"
      onClick={config.onClick}
      className={cn(
        'fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full shadow-lg sm:hidden',
        className,
      )}
    >
      {config.icon}
      <span className="sr-only">{config.label}</span>
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Hook + componente para páginas
// ---------------------------------------------------------------------------

/**
 * Registra um FAB mobile na página atual.
 *
 * @example
 * useFab({ icon: <Plus className="h-6 w-6" />, label: "Novo", onClick: handleNew });
 */
export function useFab(config: FabConfig) {
  const { setConfig } = useContext(FabContext);
  const onClickRef = useRef(config.onClick);
  useEffect(() => {
    onClickRef.current = config.onClick;
  });
  useEffect(() => {
    setConfig({ icon: config.icon, label: config.label, onClick: () => onClickRef.current() });
    return () => setConfig(null);
  }, [setConfig, config.icon, config.label]);
}

/**
 * Componente declarativo alternativo ao hook.
 *
 * @example
 * {showButton && <Fab icon={<Plus />} label="Novo" onClick={handleNew} />}
 */
export function Fab({ icon, label, onClick }: FabConfig) {
  useFab({ icon, label, onClick });
  return null;
}
